import React, { useEffect, useRef, useState } from "react";
import { Flex } from "@jsq/ds/core";
import { TreeWrapper, TreeContainer } from "./style_vanilla.css";
import { TreeEdge } from "./TreeEdge";
import { TreeNode } from "./TreeNode";
import { TreeConfig, EdgeStyle, Node } from "./types";
import { edgeConfig } from "./util";

export type TreeProps = {
  data: Node;
  treeConfig?: TreeConfig;
  edgeStyle?: EdgeStyle;
  rootEdgeStyle?: EdgeStyle;
};

export const TreeComponent = (props: TreeProps) => {
  const treeWrapper = useRef(null);
  const [containerBounds, setContainerBounds] = useState<DOMRect>();

  useEffect(() => {
    const element = treeWrapper.current;
    const observer = new ResizeObserver((entries) => {
      /**
       * Retrieves the bounding rectangle of the observed element from the first entry.
       * Since a single {@link ResizeObserver} instance can track multiple elements,
       * the callback receives an array of entries representing all resized elements.
       *
       * However, since we're only observing the TreeWrapper element, the callback will always contain a single entry,
       * allowing us to safely access the first item in the list.
       */
      const newBounds = entries[0]?.target.getBoundingClientRect();
      setContainerBounds((prev) =>
        !prev || Math.abs(prev.width - newBounds.width) > 1 ? newBounds : prev,
      );
    });

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.disconnect();
      }
    };
  }, []);

  /**
   * Render the error message if data is missing
   */
  if (!props.data) {
    return <div role="alert">Failed to render tree as `data` is missing</div>;
  }

  /**
   * Default tree config if we don't have a custom one
   */
  const treeConfig = props.treeConfig || {
    edgeStart: 20,
    edgeWidth: 96,
    labelStart: 20,
  };

  return (
    <Flex className={TreeContainer}>
      <div ref={treeWrapper} className={TreeWrapper}>
        <TreeNode treeNodes={[props.data]} level={0} treeConfig={treeConfig} />
      </div>
      <TreeEdge
        edgeConfigs={edgeConfig(props.data, props.edgeStyle, props.rootEdgeStyle)}
        treeConfig={treeConfig}
        containerBounds={containerBounds}
      />
    </Flex>
  );
};
